import localforage from 'localforage';

/**
 * @class FJMediaCacheStore
 * @classdesc 媒体资源缓存管理器
 */
let FJMediaCacheStore = (function () {
  let _caches = [];
  let _referenceStore = null;
  let _memoryData = {};  // 存储indexeddb失败时会将数据缓存到这里

  const EVENT = {
    RETAIN: 'RETAIN',
    RELEASE: 'RELEASE',
  };

  /**
   * @description 获取cache
   * @param {string | number} id
   * @return {MediaCache}
   */
  function getCache(id) {
    return _caches.find((item) => {
      return item.getId().toString() === id.toString()
    });
  }

  /**
   * @description 是否为有效id
   * @param {string | number} id
   * @return {boolean|boolean}
   */
  function isValidId(id) {
    return id !== null && typeof id !== 'undefined'
  }

  /**
   * @description 媒体缓存构造函数
   * @param {string | number} id
   * @return {Promise<MediaCache>}
   * @constructor
   */
  function MediaCache(id) {

    this._id = id;
    this._blobUrl = '';
    this._data = null;

    /**
     * @return {string}
     */
    this.getId = () => {
      return this._id.toString();
    };

    /**
     * @description 返回资源的 blobUrl
     * @return {string | null}
     */
    this.getUrl = () => {
      return this._blobUrl
    };

    /**
     * @return {null|MediaCache}
     */
    this.hasData = function () {
      return this._data;
    };

    /**
     * @description 初始化数据和资源链接
     * @param data
     * @return {Promise<void>}
     */
    this.initData = async function (data) {
      let id = this.getId();
      this._data = data || this._data || await localforage.getItem(id);
      this._blobUrl = window.URL.createObjectURL(this._data.data);
    };

    /**
     * @return {Object}
     */
    this.getData = () => {
      return this._data
    };

    /**
     * @return {*}
     */
    this.getBlob = () => {
      return this._data && this._data.data;
    };

    /**
     * @description 设置媒体缓存的具体数据并更新 timeStore 的相同 id 的时间
     * @param {Object} data
     * @return {Promise<object>}
     */
    this.setData = async (data) => {
      if (!data) {
        return Promise.reject(new Error('arguments error'));
      } else {
        let id = this.getId();
        await this.initData(data);
        try {
          await localforage.setItem(id, data);

          // 兼容隐身模式
          this._data = data;
        } catch (e) {
          _memoryData[id] = data;
        }
      }
    };

    /**
     * @description 清除媒体缓存对象并删除indexeddb中的资源
     */
    this.clear = () => {
      if (this._blobUrl) {
        window.URL.revokeObjectURL(this._blobUrl);
      }
      this._blobUrl = null;
      this._data = null;
      let index = _caches.indexOf(this);
      let id = this.getId();
      if (index !== -1) {
        localforage.removeItem(id);
        _referenceStore.removeItem(id);
        _caches.splice(index, 1);
      }
    };
  }

  return {
    /**
     * @description 初始化媒体缓存管理器
     * @return {Promise<void>}
     */
    init: async function () {
      localforage.config({
        name: 'local-storage',
        version: 1.0,
        storeName: 'assets', // Should be alphanumeric, with underscores.
        description: 'Assets store at local storage.'
      });
      _referenceStore = localforage.createInstance({
        name: 'reference-storage',
        version: 1.0,
        storeName: 'reference', // Should be alphanumeric, with underscores.
        description: 'Update reference of caches data at local storage.'
      });

      try {
        let keys = await localforage.keys();
        for (let i = 0; i < keys.length; i++) {
          let cache = new MediaCache(keys[i]);
          _caches.push(cache);
        }
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * @param {string | number} id
     * @return {MediaCache}
     */
    hasMediaCache: function (id) {
      return getCache(id);
    },

    /**
     * @param {string | number} id
     * @return {MediaCache}
     */
    getMediaCache: function (id) {
      return getCache(id);
    },

    /**
     * @param {string | number} id
     * @param {File | Blob} data
     * @param {Object} info
     */
    setMediaCache: async function (id, data, info) {
      if (!isValidId(id)) {
        return;
      }
      let idStr = id.toString();
      if (this.getMediaCache(idStr)) {
        let cache = this.getMediaCache(idStr);
        if (!cache.getUrl()) {
          await cache.initData();
        }
        return cache;
      }
      let cache = new MediaCache(idStr);
      try {
        await cache.setData({data, ...info});
        _caches.push(cache);
        return cache;
      } catch (e) {
        console.log(e);
        return false;
      }
    },

    /**
     * @param {string} id
     */
    deleteMediaCache: function (id) {
      let cache = this.getMediaCache(id);
      if (cache) {
        cache.clear();
      }
    },

    /**
     * @description 事件类型
     * @return {{RETAIN: string, RELEASE: string}}
     */
    get eventType () {
      return EVENT;
    },

    /**
     * @description 媒体缓存的事件机制
     * @param {string} type
     * @param {string | number} id
     */
    dispatch: function (type, id) {
      let idStr = id.toString();
      if (_memoryData[idStr]) {
        return;
      }
      switch (type) {
        case EVENT.RETAIN:
          _referenceStore.getItem(idStr).then(referenceData => {
            _referenceStore.setItem(idStr, {count: referenceData && referenceData.count ? referenceData.count + 1 : 1});
          }).catch(e => {
            console.log(e);
            _referenceStore.setItem(idStr, {count: 1});
          });
          break;
        case EVENT.RELEASE:
          _referenceStore.getItem(idStr).then(referenceData => {
            if (referenceData && referenceData.count) {
              _referenceStore.setItem(idStr, {count: referenceData.count - 1});
            } else {
              this.deleteMediaCache(idStr);
            }
          }).catch(e => {
            console.log(e);
            this.deleteMediaCache(idStr);
          });
          break;
        default:
      }
    },

    /**
     * @description 返回剩余可存储空间
     * @return {Promise<unknown>}
     */
    getMinStorageToRelease: function () {
      return new Promise((resolve) => {
        if (navigator.webkitTemporaryStorage) {
          navigator.webkitTemporaryStorage.queryUsageAndQuota((usedSpace, restSpace) => {
            resolve(restSpace - usedSpace);
          }, (e) => {
            resolve(0);
          })
        } else {
          resolve(0);
        }
      })
    },

    /**
     * @description 是否存储于_memoryData中
     * @param id
     * @return {boolean}
     */
    hasMemoryData: function (id) {
      return !!_memoryData[id];
    }
  };
})();

export default FJMediaCacheStore;
